import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '../api/client';
import { SkeletonBar, Button, Textarea } from '../components/ui';
import { T } from '../styles/tokens';

// ═══ Design Tokens ═══
const theme = {
  bg: T.bg, bgCard: T.bgCard, bgHover: T.bgSecondary, bgSelected: '#162040',
  border: T.border, borderActive: T.primary, blue: T.primary, indigo: T.textSecondary,
  white: T.text, light: T.textMuted, muted: T.textMuted, success: T.success,
  error: T.danger, warning: T.warning,
  gradient: 'linear-gradient(135deg, #2F6BFF 0%, #6D4CFF 100%)',
  gradientSubtle: `linear-gradient(135deg, ${T.primaryBg} 0%, ${T.accentBg} 100%)`,
};

// ═══ Types ═══
interface FileEntry {
  id?: number;
  file_path: string;
  size_bytes?: number;
  file_hash?: string;
  version?: number;
  modified_at?: string;
  mime_type?: string;
}

interface Runner {
  id: string;
  name: string;
  status: string;
  last_heartbeat_at?: string;
  last_seen_at?: string;
}

interface Category {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  matcher: (path: string) => boolean;
}

// ═══ Categories ═══
const CATEGORIES: Category[] = [
  {
    id: 'brain',
    name: 'Cérebro do Agente',
 icon: '',
    color: T.textSecondary,
    description: 'Identidade, memória, configuração do agente',
    matcher: (p) => {
      const name = p.split('/').pop()?.toLowerCase() || '';
      const brainFiles = ['agents.md', 'soul.md', 'user.md', 'identity.md', 'memory.md',
        'tools.md', 'heartbeat.md', 'bootstrap.md', 'boot.md'];
      if (brainFiles.includes(name)) return true;
      if (p.startsWith('.env') || name === '.env' || name === '.env.example') return true;
      if (name === 'config.json' || name === 'config.yaml' || name === 'config.yml') return true;
      return false;
    },
  },
  {
    id: 'memory',
    name: 'Memórias',
 icon: '',
    color: T.success,
    description: 'Diários, decisões, tópicos salvos',
    matcher: (p) => p.startsWith('memory/') || p.includes('/memory/'),
  },
  {
    id: 'reports',
    name: 'Relatórios & Tarefas',
 icon: '',
    color: T.warning,
    description: 'Relatórios diários, changelogs, logs',
    matcher: (p) => {
      const name = p.split('/').pop()?.toLowerCase() || '';
      if (p.startsWith('relatorios/') || p.includes('/relatorios/')) return true;
      if (name.startsWith('changelog') || name.startsWith('relatorio')) return true;
      if (p.startsWith('docs/') && (name.endsWith('.md') || name.endsWith('.html'))) return true;
      if (name.includes('card-template') || name.includes('organizacao')) return true;
      return false;
    },
  },
  {
    id: 'documents',
    name: 'Documentos',
 icon: '',
    color: T.primary,
    description: 'PDFs, HTMLs, documentação',
    matcher: (p) => {
      const ext = p.split('.').pop()?.toLowerCase() || '';
      if (['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv'].includes(ext)) return true;
      if (ext === 'html' && !p.includes('elon-tools/')) return true;
      if (p.includes('MANUAIS') || p.includes('PROMPTS') || p.includes('MAPA-MENTAL')) return true;
      return false;
    },
  },
  {
    id: 'images',
    name: 'Imagens',
 icon: '',
    color: T.danger,
    description: 'Fotos, ícones, assets visuais',
    matcher: (p) => {
      const ext = p.split('.').pop()?.toLowerCase() || '';
      return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico', 'bmp'].includes(ext);
    },
  },
  {
    id: 'logs',
    name: 'Logs',
 icon: '',
    color: T.warning,
    description: 'Logs de sistema, execução, erros',
    matcher: (p) => {
      const name = p.split('/').pop()?.toLowerCase() || '';
      if (p.startsWith('logs/') || p.includes('/logs/')) return true;
      if (name.endsWith('.log') || name.includes('log')) return true;
      if (name.includes('debug') || name.includes('error')) return true;
      return false;
    },
  },
  {
    id: 'scripts',
    name: 'Scripts & Automações',
 icon: '',
    color: T.accent,
    description: 'Scripts de automação, cron jobs, utilitários',
    matcher: (p) => {
      if (p.startsWith('scripts/') || p.includes('/scripts/')) return true;
      const ext = p.split('.').pop()?.toLowerCase() || '';
      if (['sh', 'bash'].includes(ext)) return true;
      const name = p.split('/').pop()?.toLowerCase() || '';
      if (name.includes('cron') || name.includes('automat') || name.includes('consolidar') || name.includes('uninstall')) return true;
      return false;
    },
  },
  {
    id: 'backups',
    name: 'Backups',
 icon: '',
    color: T.accent,
    description: 'Backups de workflows, configs anteriores',
    matcher: (p) => p.startsWith('backups/') || p.includes('/backups/') || p.includes('.bak') || p.includes('backup'),
  },
  {
    id: 'config',
    name: 'Configurações',
 icon: '',
    color: T.textMuted,
    description: 'Package.json, tsconfig, gitignore, configs de projeto',
    matcher: (p) => {
      const name = p.split('/').pop()?.toLowerCase() || '';
      if (['package.json', 'package-lock.json', 'tsconfig.json', 'tsconfig.base.json',
           '.gitignore', '.nvmrc', 'wrangler.toml', 'vitest.config.ts', 'vite.config.ts',
           'tailwind.config.js', 'postcss.config.js', 'eslint.config.js'].includes(name)) return true;
      const ext = p.split('.').pop()?.toLowerCase() || '';
      if (['toml', 'yml', 'yaml'].includes(ext)) return true;
      return false;
    },
  },
  {
    id: 'code',
    name: 'Código Fonte',
 icon: '',
    color: '#06B6D4',
    description: 'APIs, frontend, componentes, serviços',
    matcher: (p) => {
      if (p.startsWith('elon-tools/') || p.startsWith('elon-system-dev/') || p.startsWith('runner/')) return true;
      const ext = p.split('.').pop()?.toLowerCase() || '';
      return ['ts', 'tsx', 'js', 'jsx', 'py', 'sql', 'css', 'scss'].includes(ext);
    },
  },
  {
    id: 'other',
    name: 'Outros',
 icon: '',
    color: T.textMuted,
    description: 'Arquivos diversos',
    matcher: () => true, // catch-all
  },
];

function categorizeFile(path: string): string {
  for (const cat of CATEGORIES) {
    if (cat.id !== 'other' && cat.matcher(path)) return cat.id;
  }
  return 'other';
}

// ═══ Helpers ═══
const EXT_ICONS: Record<string, string> = {
 ts: '', tsx: '', js: '', jsx: '', json: '', md: '', html: '',
 css: '', sql: '', sh: '', yml: '', yaml: '', env: '', txt: '',
 png: '', jpg: '', svg: '', py: '', lock: '', gitignore: '',
};

function getIcon(name: string): string {
  const ext = name.split('.').pop()?.toLowerCase() || '';
 return EXT_ICONS[ext] || '';
}

function formatSize(bytes?: number): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function timeAgo(date?: string): string {
  if (!date) return '—';
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `${mins}min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

// ═══ Main Component ═══
export function FileExplorer({ path: _routePath }: { path?: string }) {
  const [runners, setRunners] = useState<Runner[]>([]);
  const [selectedRunner, setSelectedRunner] = useState<Runner | null>(null);
  const [files, setFiles] = useState<FileEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileEntry | null>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    (async () => {
      try {
        // Fetch v2 runners
        let v2Runners: Runner[] = [];
        try {
          let data: { runners: Runner[] };
          try { data = await api.get<{ runners: Runner[] }>('/workspace/runners'); }
          catch { data = await api.get<{ runners: Runner[] }>('/runners'); }
          v2Runners = data.runners || [];
        } catch {}

        // Fetch v3 runners
        let v3Runners: Runner[] = [];
        try {
          const v3res = await api.get<{ runners: Runner[] }>('/runner-v3/list');
          // Handle both unwrapped {runners:[]} and legacy double-wrapped {success:true, data:{runners:[]}}
          const v3list = (v3res as any)?.runners || (v3res as any)?.data?.runners || [];
          v3Runners = v3list.map((r: any) => ({
            ...r,
            last_heartbeat_at: r.last_seen_at,
            runner_mode: 'v3',
          }));
        } catch {}

        const allRunners = [...v2Runners, ...v3Runners];
        // Show all runners (not just online) so user can browse stored files
        const sorted = allRunners.sort((a, b) => {
          // Online runners first
          const aOnline = a.status === 'online' ? 1 : 0;
          const bOnline = b.status === 'online' ? 1 : 0;
          if (aOnline !== bOnline) return bOnline - aOnline;
          // Then by last seen
          const aTime = new Date(a.last_heartbeat_at || a.last_seen_at || 0).getTime();
          const bTime = new Date(b.last_heartbeat_at || b.last_seen_at || 0).getTime();
          return bTime - aTime;
        });
        setRunners(sorted);
        if (sorted.length > 0 && sorted[0]) { setSelectedRunner(sorted[0]); loadFiles(sorted[0].id); }
      } catch (e) { console.error('Erro ao carregar runners:', e); }
    })();
  }, []);

  const loadFiles = async (runnerId: string) => {
    setLoading(true);
    try {
      const data = await api.get<{ files: FileEntry[] }>(`/workspace/runners/${runnerId}/files`);
      setFiles(data.files || []);
    } catch (err) {
      console.error(`[FileExplorer] Erro ao carregar arquivos do runner ${runnerId}:`, err);
      setFiles([]);
    } finally { setLoading(false); }
  };

  const loadFileContent = async (file: FileEntry) => {
    if (!selectedRunner) return;
    setSelectedFile(file);
    setLoadingFile(true);
    setFileContent(null);
    setEditMode(false);
    try {
      const data = await api.get<{ file: { content: string } }>(`/workspace/runners/${selectedRunner.id}/files/${file.file_path}`);
      setFileContent(data.file?.content ?? '');
    } catch { setFileContent('// Erro ao carregar arquivo'); }
    finally { setLoadingFile(false); }
  };

  const saveFile = async () => {
    if (!selectedRunner || !selectedFile) return;
    setSaving(true); setSaved(false);
    try {
      await api.post(`/workspace/runners/${selectedRunner.id}/files/${selectedFile.file_path}`, {
        content: editContent, reason: 'Editado via Arquivos',
      });
      setFileContent(editContent);
      setEditMode(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      loadFiles(selectedRunner.id);
    } catch { alert('Erro ao salvar arquivo'); }
    finally { setSaving(false); }
  };

  // Categorized files
  const categorizedFiles = new Map<string, FileEntry[]>();
  CATEGORIES.forEach(c => categorizedFiles.set(c.id, []));
  const filteredFiles = searchQuery
    ? files.filter(f => f.file_path.toLowerCase().includes(searchQuery.toLowerCase()))
    : files;
  filteredFiles.forEach(f => {
    const catId = categorizeFile(f.file_path);
    categorizedFiles.get(catId)?.push(f);
  });

  // Category stats
  const catStats = CATEGORIES.map(c => ({
    ...c,
    count: categorizedFiles.get(c.id)?.length || 0,
    size: (categorizedFiles.get(c.id) || []).reduce((s, f) => s + (f.size_bytes || 0), 0),
  })).filter(c => c.count > 0);

  const currentFiles = activeCategory ? (categorizedFiles.get(activeCategory) || []) : [];
  const totalSize = files.reduce((s, f) => s + (f.size_bytes || 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: theme.bg, color: theme.white }}>

      {/* ═══ Header ═══ */}
      <div style={{
        padding: '28px 32px 24px', borderBottom: `1px solid ${theme.border}`,
        background: `linear-gradient(180deg, ${theme.bgCard} 0%, ${theme.bg} 100%)`, flexShrink: 0,
      }}>
        {/* Top row: title + runner */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, color: theme.white, letterSpacing: -0.5 }}>
              Meus Arquivos
            </h1>
            <p style={{ fontSize: 14, color: theme.muted, margin: '4px 0 0' }}>
              {files.length} arquivos • {formatSize(totalSize)} • sincronizado em tempo real
            </p>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: theme.bgCard, border: `1px solid ${theme.border}`,
            borderRadius: 12, padding: '10px 16px',
          }}>
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: (() => {
                if (!selectedRunner) return theme.muted;
                const ls = selectedRunner.last_heartbeat_at || selectedRunner.last_seen_at;
                const isOnline = selectedRunner.status === 'online' && ls && (Date.now() - new Date(ls).getTime()) < 120_000;
                return isOnline ? theme.success : theme.error;
              })(),
              boxShadow: (() => {
                if (!selectedRunner) return 'none';
                const ls = selectedRunner.last_heartbeat_at || selectedRunner.last_seen_at;
                const isOnline = selectedRunner.status === 'online' && ls && (Date.now() - new Date(ls).getTime()) < 120_000;
                return isOnline ? `0 0 8px ${theme.success}60` : 'none';
              })(),
            }} />
            <select
              value={selectedRunner?.id || ''}
              onChange={(e: any) => {
                const r = runners.find(r => r.id === e.target.value);
                if (r) { setSelectedRunner(r); loadFiles(r.id); setSelectedFile(null); setActiveCategory(null); }
              }}
              style={{
                background: 'transparent', color: theme.white, border: 'none',
                fontSize: 14, fontWeight: 600, outline: 'none', cursor: 'pointer',
              }}
            >
              {runners.map(r => (
                <option key={r.id} value={r.id} style={{ background: theme.bgCard }}>{r.name}</option>
              ))}
            </select>
            <span style={{ fontSize: 12, color: (() => {
              if (!selectedRunner) return theme.muted;
              const ls = selectedRunner.last_heartbeat_at || selectedRunner.last_seen_at;
              const isOnline = selectedRunner.status === 'online' && ls && (Date.now() - new Date(ls).getTime()) < 120_000;
              return isOnline ? theme.success : theme.error;
            })(), fontWeight: 500 }}>{(() => {
              if (!selectedRunner) return 'N/A';
              const ls = selectedRunner.last_heartbeat_at || selectedRunner.last_seen_at;
              const isOnline = selectedRunner.status === 'online' && ls && (Date.now() - new Date(ls).getTime()) < 120_000;
              return isOnline ? 'Online' : 'Offline';
            })()}</span>
          </div>
        </div>

        {/* Search bar — full width, prominent */}
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Buscar arquivos por nome, pasta ou conteúdo..."
            value={searchQuery}
            onInput={(e: any) => { setSearchQuery(e.target.value); setActiveCategory(null); setSelectedFile(null); }}
            style={{
              width: '100%', background: theme.bgCard, color: theme.white,
              border: `1px solid ${theme.border}`, borderRadius: 12,
              padding: '14px 20px 14px 48px', fontSize: 15, outline: 'none',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box',
            }}
            onFocus={(e: any) => { e.target.style.borderColor = theme.blue; }}
            onBlur={(e: any) => { e.target.style.borderColor = theme.border; }}
          />
          <span style={{
            position: 'absolute', left: 18, top: '50%', transform: 'translateY(-50%)',
            fontSize: 18, pointerEvents: 'none',
 }}></span>
        </div>
      </div>

      {/* ═══ Main Content ═══ */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>

        {/* Left: Categories + File List */}
        <div style={{ width: 380, flexShrink: 0, borderRight: `1px solid ${theme.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* Categories Grid or Search Results */}
          {!activeCategory && !searchQuery ? (
            <div style={{ padding: 16, overflowY: 'auto', flex: 1, minHeight: 0 }}>

              {/* ═══ Recentes ═══ */}
              {files.length > 0 && (() => {
                const recent = [...files]
                  .filter(f => f.modified_at)
                  .sort((a, b) => new Date(b.modified_at!).getTime() - new Date(a.modified_at!).getTime())
                  .slice(0, 8);
                return (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: theme.muted, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
 Recentes
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {recent.map(f => {
                        const name = f.file_path.split('/').pop() || f.file_path;
                        const dir = f.file_path.split('/').slice(0, -1).join('/');
                        const cat = CATEGORIES.find(c => c.id !== 'other' && c.matcher(f.file_path)) ?? CATEGORIES.at(-1)!;
                        return (
                          <div
                            key={f.file_path}
                            onClick={() => loadFileContent(f)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 10,
                              padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                              transition: 'all 0.15s', background: 'transparent',
                            }}
                            onMouseEnter={(e: any) => { e.currentTarget.style.background = theme.bgHover; }}
                            onMouseLeave={(e: any) => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            <span style={{ fontSize: 16, flexShrink: 0 }}>{getIcon(name)}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 13, fontWeight: 500, color: theme.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {name}
                                </span>
                                <span style={{
                                  fontSize: 9, fontWeight: 700, color: cat.color,
                                  background: `${cat.color}18`, padding: '1px 6px', borderRadius: 6,
                                  whiteSpace: 'nowrap', flexShrink: 0,
                                }}>
                                  {cat.icon} {cat.name}
                                </span>
                              </div>
                              {dir && <div style={{ fontSize: 10, color: theme.muted, marginTop: 1 }}>{dir}</div>}
                            </div>
                            <div style={{ fontSize: 11, color: theme.muted, flexShrink: 0 }}>{timeAgo(f.modified_at)}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* ═══ Categorias ═══ */}
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.muted, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
 Categorias
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {catStats.map(cat => (
                  <div
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    style={{
                      padding: '14px 16px', borderRadius: 12,
                      background: theme.bgCard, border: `1px solid ${theme.border}`,
                      cursor: 'pointer', transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = cat.color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.transform = 'none'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 22 }}>{cat.icon}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: cat.color,
                        background: `${cat.color}15`, padding: '2px 8px', borderRadius: 10,
                      }}>
                        {cat.count}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.white }}>{cat.name}</div>
                    <div style={{ fontSize: 10, color: theme.muted, marginTop: 2 }}>{cat.description}</div>
                    <div style={{ fontSize: 10, color: theme.muted, marginTop: 4 }}>{formatSize(cat.size)}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* File List (category selected or search active) */
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
              {/* Category header */}
              <div style={{
                padding: '12px 16px', borderBottom: `1px solid ${theme.border}`,
                display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
              }}>
                <Button variant="ghost" size="xs" onClick={() => { setActiveCategory(null); setSelectedFile(null); setSearchQuery(''); }}>
                  ← Voltar
                </Button>
                {activeCategory && (
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {CATEGORIES.find(c => c.id === activeCategory)?.icon} {CATEGORIES.find(c => c.id === activeCategory)?.name}
                    <span style={{ color: theme.muted, fontWeight: 400, marginLeft: 8 }}>
                      {currentFiles.length || filteredFiles.length} arquivos
                    </span>
                  </span>
                )}
                {searchQuery && !activeCategory && (
                  <span style={{ fontSize: 13, color: theme.muted }}>
 {filteredFiles.length} resultado{filteredFiles.length !== 1 ? 's' : ''} para "{searchQuery}"
                  </span>
                )}
              </div>

              {/* File list */}
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                {(activeCategory ? currentFiles : filteredFiles)
                  .sort((a, b) => a.file_path.localeCompare(b.file_path))
                  .map(f => {
                    const name = f.file_path.split('/').pop() || f.file_path;
                    const dir = f.file_path.split('/').slice(0, -1).join('/');
                    const isSelected = selectedFile?.file_path === f.file_path;
                    return (
                      <div
                        key={f.file_path}
                        onClick={() => loadFileContent(f)}
                        style={{
                          padding: '10px 16px', cursor: 'pointer',
                          background: isSelected ? theme.bgSelected : 'transparent',
                          borderLeft: isSelected ? `3px solid ${theme.blue}` : '3px solid transparent',
                          borderBottom: `1px solid ${theme.border}08`,
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={(e: any) => { if (!isSelected) e.currentTarget.style.background = theme.bgHover; }}
                        onMouseLeave={(e: any) => { if (!isSelected) e.currentTarget.style.background = isSelected ? theme.bgSelected : 'transparent'; }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 14, flexShrink: 0 }}>{getIcon(name)}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: theme.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {name}
                            </div>
                            {dir && (
                              <div style={{ fontSize: 10, color: theme.muted, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {dir}
                              </div>
                            )}
                          </div>
                          <div style={{ fontSize: 10, color: theme.muted, textAlign: 'right', flexShrink: 0 }}>
                            <div>{formatSize(f.size_bytes)}</div>
                            <div>{timeAgo(f.modified_at)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* Right: File Viewer */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {selectedFile ? (
            <>
              {/* File header bar */}
              <div style={{
                padding: '10px 20px', borderBottom: `1px solid ${theme.border}`,
                display: 'flex', alignItems: 'center', gap: 12, background: theme.bgCard, flexShrink: 0,
              }}>
                <span style={{ fontSize: 14 }}>{getIcon(selectedFile.file_path.split('/').pop() || '')}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.white, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {selectedFile.file_path.split('/').pop()}
                  </div>
                  <div style={{ fontSize: 11, color: theme.muted }}>{selectedFile.file_path}</div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
 {saved && <span style={{ fontSize: 12, color: theme.success }}>Salvo!</span>}
                  {!editMode ? (
                    <>
                    <Button variant="ghost" size="sm" onClick={() => {
                        const blob = new Blob([fileContent || ''], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = selectedFile?.file_path.split('/').pop() || 'file';
                        a.click(); URL.revokeObjectURL(url);
                      }}>
 Baixar
                    </Button>
                    <Button size="sm" onClick={() => { setEditMode(true); setEditContent(fileContent || ''); }}>
 Editar
                    </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="success" size="sm" onClick={saveFile} disabled={saving} loading={saving}>
 {saving ? '' : ''} Salvar
                      </Button>
                      <Button variant="ghost" onClick={() => setEditMode(false)}
                        style={{
                          background: 'transparent', color: theme.muted, border: `1px solid ${theme.border}`,
                          borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer',
                        }}>
                                              </Button>
                    </>
                  )}
                  <div style={{ fontSize: 11, color: theme.muted }}>
                    {formatSize(selectedFile.size_bytes)} • {timeAgo(selectedFile.modified_at)}
                  </div>
                </div>
              </div>

              {/* File content */}
              <div style={{ flex: 1, overflow: 'auto' }}>
                {loadingFile ? (
                  <div style={{ padding: 40, textAlign: 'center', color: theme.muted }}>
 <div style={{ fontSize: 32, marginBottom: 12 }}></div>
                    <div style={{ padding: 8 }}><SkeletonBar width="50%" height={14} /></div>
                  </div>
                ) : editMode ? (
                  <Textarea
                    ref={textareaRef}
                    value={editContent}
                    onInput={(e: any) => setEditContent(e.target.value)}
                    style={{
                      width: '100%', height: '100%', background: theme.bg, color: T.text,
                      border: 'none', outline: 'none', padding: '16px 20px', fontSize: 13,
                      fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                      lineHeight: 1.6, resize: 'none', tabSize: 2,
                    }}
                    onKeyDown={(e: any) => {
                      if (e.key === 'Tab') {
                        e.preventDefault();
                        const s = e.target.selectionStart, en = e.target.selectionEnd;
                        setEditContent(editContent.substring(0, s) + '  ' + editContent.substring(en));
                        setTimeout(() => { e.target.selectionStart = e.target.selectionEnd = s + 2; }, 0);
                      }
                      if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); saveFile(); }
                    }}
                  />
                ) : (
                  <pre style={{
                    margin: 0, padding: '16px 20px', fontSize: 13,
                    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                    lineHeight: 1.6, color: T.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {(fileContent || '').split('\n').map((line, i) => (
                      <div key={i} style={{ display: 'flex' }}>
                        <span style={{
                          color: theme.muted, width: 45, flexShrink: 0, textAlign: 'right',
                          paddingRight: 16, userSelect: 'none', fontSize: 12, opacity: 0.4,
                        }}>{i + 1}</span>
                        <span>{line || ' '}</span>
                      </div>
                    ))}
                  </pre>
                )}
              </div>
            </>
          ) : (
            /* Welcome state */
            <div style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', padding: 40,
            }}>
              <div style={{
                width: 90, height: 90, borderRadius: 24, background: theme.gradientSubtle,
                border: `1px solid ${theme.border}`, display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 40, marginBottom: 24,
 }}></div>
              <h2 style={{ color: theme.white, fontSize: 20, margin: '0 0 8px', fontWeight: 700 }}>
                Workspace Arquivos
              </h2>
              <p style={{ color: theme.muted, fontSize: 14, margin: '0 0 32px', textAlign: 'center', maxWidth: 400 }}>
                Navegue pelos arquivos do seu workspace organizados por categoria.
                Selecione uma categoria à esquerda para começar.
              </p>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
                {[
 { icon: '', text: 'Ctrl+S para salvar' },
 { icon: '', text: 'Busca global' },
 { icon: '', text: 'Editor inline' },
 { icon: '', text: 'Sync automático' },
                ].map((tip, i) => (
                  <div key={i} style={{
                    padding: '10px 16px', background: theme.bgCard, borderRadius: 10,
                    border: `1px solid ${theme.border}`, fontSize: 12, color: theme.light,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span>{tip.icon}</span> {tip.text}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const FileExplorerPage = FileExplorer;
